/**
 * Seedance 2.0 master configuration page (admin-only).
 *
 * Stores the agency-wide Seedance API key (encrypted on the backend) and
 * the default video generation parameters. The key itself is never sent
 * back from the server — the page only shows a redacted hint and a
 * verify-key button that pings the Seedance API to confirm auth.
 *
 * Per-client video generation (the actual `Generate video with AI` button
 * inside the calendar dialog) ships in a follow-up phase.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Film, KeyRound, Loader2, ShieldCheck, ShieldAlert, Trash2, ExternalLink,
  CheckCircle2, XCircle, Sparkles, Construction, FileText, Upload, Download,
} from "lucide-react";

import { API } from "@/lib/api";

const RESOLUTIONS = ["480p", "720p", "1080p", "4k"];
const ASPECT_RATIOS = ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9", "adaptive"];
const MODELS = [
  { key: "seedance-2-0",      label: "Seedance 2.0 (best quality)" },
  { key: "seedance-2-0-fast", label: "Seedance 2.0 Fast (cheaper, faster)" },
];

export default function SeedanceIntegrationsPage() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [apiKey, setApiKey] = useState("");  // write-only; never populated from server
  const [showKey, setShowKey] = useState(false);
  const [uploadingLicense, setUploadingLicense] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/seedance");
      setCfg(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Seedance config");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        default_model: cfg.default_model,
        default_resolution: cfg.default_resolution,
        default_duration: Number(cfg.default_duration),
        default_aspect_ratio: cfg.default_aspect_ratio,
        callback_url: cfg.callback_url || null,
      };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const { data } = await api.put("/integrations/seedance", payload);
      setCfg(data);
      setApiKey("");
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/integrations/seedance/verify");
      if (data.ok) toast.success(`Verified: ${data.detail}`);
      else toast.error(data.detail);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    } finally { setVerifying(false); }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove the Seedance API key? Video generation will stop working until a new key is set.")) return;
    try {
      await api.delete("/integrations/seedance/key");
      toast.success("API key removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove key");
    }
  };

  const uploadLicense = async (file) => {
    if (!file) return;
    setUploadingLicense(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/integrations/seedance/license", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("License uploaded");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploadingLicense(false); }
  };

  const removeLicense = async () => {
    if (!window.confirm("Remove the uploaded Seedance license?")) return;
    try {
      await api.delete("/integrations/seedance/license");
      toast.success("License removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove license");
    }
  };

  if (!cfg) {
    return (
      <div className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Seedance config…
      </div>
    );
  }

  return (
    <section className="p-6 space-y-5 max-w-4xl" data-testid="seedance-master-page">
      <div className="flex items-start justify-between gap-3 flex-wrap border-b border-zinc-200 pb-3">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-sm grid place-items-center bg-[#E11D48]/10">
            <Film className="h-4 w-4 text-[#E11D48]" />
          </span>
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tight">
              Seedance · AI Video Generation
            </h2>
            <p className="text-[12px] text-zinc-500">
              Generate text-to-video, image-to-video and reference-to-video
              clips directly from the calendar.{" "}
              <a className="underline" href="https://seevio.ai/api-docs"
                target="_blank" rel="noreferrer">
                API docs <ExternalLink className="inline h-3 w-3" />
              </a>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {cfg.has_api_key
            ? <Badge className="rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">
                <ShieldCheck className="h-3 w-3 mr-1" /> Key configured
              </Badge>
            : <Badge className="rounded-sm bg-amber-50 text-amber-700 border-amber-200">
                <ShieldAlert className="h-3 w-3 mr-1" /> No key
              </Badge>}
          {cfg.key_kind && (
            <Badge variant="outline" className="rounded-sm font-mono text-[10px]">
              {cfg.key_kind}
            </Badge>
          )}
        </div>
      </div>

      {/* API key block */}
      <Card className="rounded-sm">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-1.5">
            <KeyRound className="h-3.5 w-3.5 text-[#E11D48]" />
            <h3 className="font-display font-bold text-sm">API key</h3>
          </div>
          {cfg.has_api_key && (
            <div className="flex items-center gap-2 text-[12px] text-zinc-700"
              data-testid="seedance-key-hint">
              <span className="label-overline">Current key</span>
              <code className="font-mono bg-zinc-100 px-2 py-0.5 rounded-sm">
                {cfg.api_key_hint}
              </code>
              <Button size="sm" variant="ghost" className="rounded-sm h-7 text-red-600"
                onClick={clearKey} data-testid="seedance-clear-key">
                <Trash2 className="h-3 w-3 mr-1" /> Remove
              </Button>
            </div>
          )}
          <div>
            <Label className="label-overline">
              {cfg.has_api_key ? "Rotate key (paste a new one)" : "Paste your Seedance API key"}
            </Label>
            <div className="mt-1 flex items-center gap-2">
              <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                type={showKey ? "text" : "password"}
                placeholder="sk_live_…  or  sk_test_…"
                className="rounded-sm h-9 text-sm font-mono flex-1"
                autoComplete="off"
                data-testid="seedance-key-input" />
              <button type="button" onClick={() => setShowKey((s) => !s)}
                className="text-[10px] font-mono text-zinc-600 hover:underline">
                {showKey ? "hide" : "show"}
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">
              Get yours from{" "}
              <a href="https://seevio.ai/dashboard/user/api-keys"
                target="_blank" rel="noreferrer" className="underline">
                seevio.ai → API keys <ExternalLink className="inline h-3 w-3" />
              </a>. The key is encrypted at rest and never sent back from
              the server — we only show the first/last few chars to confirm
              which key is loaded.
            </p>
          </div>
          {cfg.verified_at && (
            <div className={`flex items-center gap-1.5 text-[11px] ${
              cfg.verified_ok ? "text-emerald-700" : "text-red-700"
            }`} data-testid="seedance-verify-status">
              {cfg.verified_ok
                ? <CheckCircle2 className="h-3 w-3" />
                : <XCircle className="h-3 w-3" />}
              Last verified {new Date(cfg.verified_at).toLocaleString("en-AU")}
              {" · "}{cfg.verified_ok ? "OK" : "Failed"}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Defaults block */}
      <Card className="rounded-sm">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-[#E11D48]" />
            <h3 className="font-display font-bold text-sm">Generation defaults</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="label-overline">Model</Label>
              <Select value={cfg.default_model}
                onValueChange={(v) => setCfg({ ...cfg, default_model: v })}>
                <SelectTrigger className="mt-1 rounded-sm h-9 text-sm"
                  data-testid="seedance-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODELS.map((m) => (
                    <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Resolution</Label>
              <Select value={cfg.default_resolution}
                onValueChange={(v) => setCfg({ ...cfg, default_resolution: v })}>
                <SelectTrigger className="mt-1 rounded-sm h-9 text-sm"
                  data-testid="seedance-resolution">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Duration (sec)</Label>
              <Input type="number" min="4" max="15" value={cfg.default_duration}
                onChange={(e) => setCfg({ ...cfg, default_duration: e.target.value })}
                className="mt-1 rounded-sm h-9 text-sm font-mono"
                data-testid="seedance-duration" />
            </div>
            <div>
              <Label className="label-overline">Aspect ratio</Label>
              <Select value={cfg.default_aspect_ratio}
                onValueChange={(v) => setCfg({ ...cfg, default_aspect_ratio: v })}>
                <SelectTrigger className="mt-1 rounded-sm h-9 text-sm"
                  data-testid="seedance-aspect">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASPECT_RATIOS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-overline">Webhook URL (optional)</Label>
            <Input value={cfg.callback_url || ""}
              onChange={(e) => setCfg({ ...cfg, callback_url: e.target.value })}
              className="mt-1 rounded-sm h-9 text-sm font-mono"
              placeholder="https://your-domain.com/api/seedance/webhook"
              data-testid="seedance-webhook" />
            <p className="text-[11px] text-zinc-500 mt-1">
              Production should use webhooks instead of polling. We&apos;ll
              auto-host the receiver at this URL in the next phase.
            </p>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
            <Button onClick={save} disabled={busy}
              className="rounded-sm bg-[#E11D48] hover:bg-[#9F1239] text-white"
              data-testid="seedance-save">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Save changes
            </Button>
            <Button variant="outline" onClick={verify} disabled={verifying || !cfg.has_api_key}
              className="rounded-sm"
              data-testid="seedance-verify">
              {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Verify API key
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* License upload */}
      <Card className="rounded-sm">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5 text-[#E11D48]" />
            <h3 className="font-display font-bold text-sm">License document</h3>
          </div>
          <p className="text-[12px] text-zinc-500 leading-snug">
            Upload the Seedance commercial usage license (PDF) so the team
            can always reference the licensing terms — useful for client
            sign-off when delivering AI-generated video assets.
          </p>
          {cfg.license ? (
            <div className="rounded-sm border border-zinc-200 bg-zinc-50/40 p-3 flex items-center gap-3"
              data-testid="seedance-license-current">
              <div className="h-12 w-12 rounded-sm grid place-items-center bg-[#E11D48]/10 text-[#E11D48]">
                <FileText className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-display font-bold truncate">
                  {cfg.license.filename}
                </div>
                <div className="text-[10px] font-mono text-zinc-500">
                  {Math.round((cfg.license.size || 0) / 1024)} KB · {cfg.license.mime} ·
                  uploaded {new Date(cfg.license.uploaded_at).toLocaleString("en-AU")}
                  {cfg.license.uploaded_by_name && <> by {cfg.license.uploaded_by_name}</>}
                </div>
              </div>
              <a href={`${API}/integrations/seedance/license`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-mono
                  text-[#002FA7] hover:underline px-2 py-1"
                data-testid="seedance-license-download">
                <Download className="h-3 w-3" /> Download
              </a>
              <Button size="sm" variant="ghost"
                className="rounded-sm h-7 text-red-600"
                onClick={removeLicense}
                data-testid="seedance-license-remove">
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="rounded-sm border border-dashed border-zinc-300 bg-zinc-50/30 p-4 text-center"
              data-testid="seedance-license-empty">
              <FileText className="h-8 w-8 text-zinc-300 mx-auto mb-1" />
              <div className="text-[11px] text-zinc-500 mb-2">
                No license uploaded yet.
              </div>
              <label className="inline-flex items-center gap-1.5 cursor-pointer
                bg-[#E11D48] hover:bg-[#9F1239] text-white
                rounded-sm px-3 py-1.5 text-xs font-display font-semibold">
                <input type="file" accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => uploadLicense(e.target.files?.[0])}
                  data-testid="seedance-license-input" />
                {uploadingLicense ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                Upload license (PDF or image)
              </label>
              <div className="text-[10px] font-mono text-zinc-400 mt-1.5">
                Max 20 MB · accepts PDF and image files
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coming next */}
      <Card className="rounded-sm border-dashed border-zinc-300 bg-zinc-50/50">
        <CardContent className="p-5 space-y-2">
          <div className="flex items-center gap-1.5">
            <Construction className="h-3.5 w-3.5 text-amber-700" />
            <h3 className="font-display font-bold text-sm text-amber-900">
              Coming next phase
            </h3>
          </div>
          <ul className="text-[12px] text-zinc-700 list-disc ml-5 space-y-1">
            <li>
              In-calendar &quot;Generate video with AI&quot; button on any
              activity with platform = TikTok / YouTube / Reels / Story
            </li>
            <li>
              Reference assets — pull product imagery from a property&apos;s
              uploads and feed straight into Seedance as <code>image_urls</code>
            </li>
            <li>Async job tracking with live status pill + auto-attach the finished MP4 to the activity</li>
            <li>Credit usage dashboard with per-client cost attribution</li>
          </ul>
        </CardContent>
      </Card>
    </section>
  );
}
